/**
 * Class-wise Excel export
 * =======================
 * One workbook, one sheet per class+section. Each sheet has:
 *   1. Header block  – school name / address / exam / class (yellow).
 *   2. Marks table   – S.N | ADM NO. | Students Name | per-subject
 *                      (Marks | Grade) | TOTAL | % | GRADE + AVERAGE row.
 *   3. Grade matrix  – rows = grade bands + ABSENT + TOTAL,
 *                      columns = subjects + OVERALL.
 * Only subjects that actually have marks in that class/section appear.
 * A mark of 0 is treated as Absent (AB) to match the school convention.
 */

import * as XLSX from 'xlsx-js-style';
import { gradeForPercentage } from './resultsApi';

/* ------------------------------------------------------------------ */
/* Style helpers                                                       */
/* ------------------------------------------------------------------ */

const BORDER = {
  top: { style: 'thin', color: { rgb: '000000' } },
  bottom: { style: 'thin', color: { rgb: '000000' } },
  left: { style: 'thin', color: { rgb: '000000' } },
  right: { style: 'thin', color: { rgb: '000000' } },
};

const CENTER = { horizontal: 'center', vertical: 'center', wrapText: true };

const S = {
  schoolName: {
    font: { bold: true, sz: 16, color: { rgb: 'C00000' } },
    fill: { fgColor: { rgb: 'FFFF00' } },
    alignment: CENTER,
    border: BORDER,
  },
  schoolSub: {
    font: { bold: true, sz: 11 },
    fill: { fgColor: { rgb: 'FFFF00' } },
    alignment: CENTER,
    border: BORDER,
  },
  headTan: {
    font: { bold: true, sz: 10 },
    fill: { fgColor: { rgb: 'D8A77A' } },
    alignment: CENTER,
    border: BORDER,
  },
  headVert: {
    font: { bold: true, sz: 9 },
    fill: { fgColor: { rgb: 'C9BFA8' } },
    alignment: { ...CENTER, textRotation: 90 },
    border: BORDER,
  },
  sn: { font: { sz: 9 }, alignment: { horizontal: 'right', vertical: 'center' }, border: BORDER },
  name: {
    font: { sz: 9, bold: true, color: { rgb: '7B2E2E' } },
    alignment: { horizontal: 'left', vertical: 'center' },
    border: BORDER,
  },
  mark: {
    font: { sz: 9, bold: true },
    fill: { fgColor: { rgb: 'D6CFC0' } },
    alignment: CENTER,
    border: BORDER,
  },
  markPlain: { font: { sz: 9, bold: true }, alignment: CENTER, border: BORDER },
  avgLabel: { font: { sz: 9, bold: true }, alignment: { horizontal: 'left', vertical: 'center' }, border: BORDER },
  avgVal: { font: { sz: 9, bold: true }, alignment: CENTER, border: BORDER },
  blueTitle: {
    font: { bold: true, sz: 12 },
    fill: { fgColor: { rgb: 'BDD7EE' } },
    alignment: CENTER,
    border: BORDER,
  },
  gradeHead: {
    font: { bold: true, sz: 9 },
    alignment: { ...CENTER, textRotation: 90 },
    border: BORDER,
  },
  gradeLabel: { font: { bold: true, sz: 9 }, alignment: CENTER, border: BORDER },
  gradeVal: { font: { bold: true, sz: 9 }, alignment: CENTER, border: BORDER },
  bigTitle: { font: { bold: true, sz: 14 }, alignment: CENTER, border: BORDER },
  grayHead: {
    font: { bold: true, sz: 9 },
    fill: { fgColor: { rgb: 'D9D9D9' } },
    alignment: CENTER,
    border: BORDER,
  },
  graySub: {
    font: { bold: true, sz: 10 },
    fill: { fgColor: { rgb: 'D9D9D9' } },
    alignment: CENTER,
    border: BORDER,
  },
};

/** Build a styled cell object. */
const C = (v, s) => ({
  v: v === null || v === undefined ? '' : v,
  t: typeof v === 'number' ? 'n' : 's',
  s,
});

const round = (n, d = 2) => {
  const p = 10 ** d;
  return Math.round(n * p) / p;
};

/* ------------------------------------------------------------------ */
/* Per-student helpers                                                 */
/* ------------------------------------------------------------------ */

/** Grade for one subject mark entry; 0 marks = AB (absent). */
const subjectGrade = (m, grades) => {
  if (!m || !m.full) return '';
  if (!m.obtained) return 'AB';
  return gradeForPercentage((m.obtained / m.full) * 100, grades);
};

const overallGrade = (student, grades) => {
  if (!student.obtained) return 'AB';
  return gradeForPercentage(student.percentage, grades);
};

/* ------------------------------------------------------------------ */
/* Sheet builder                                                       */
/* ------------------------------------------------------------------ */

const buildSheet = (group, exam, grades, school) => {
  const { className, section, students, subjects } = group;
  const nSub = subjects.length;
  const totalCols = 3 + nSub * 2 + 3; // SN, ADM, Name, (Marks|Grade)*n, TOTAL, %, GRADE
  const last = totalCols - 1;

  const rows = [];
  const merges = [];
  const full = (r, text, style) => {
    const row = new Array(totalCols).fill(null).map(() => C('', style));
    row[0] = C(text, style);
    rows.push(row);
    merges.push({ s: { r, c: 0 }, e: { r, c: last } });
  };

  /* ---- Header block (rows 0-3) ---- */
  full(0, school?.schoolName || '', S.schoolName);
  full(1, school?.schoolAddress || '', S.schoolSub);
  full(2, exam?.Exam_Name || '', S.schoolSub);
  full(3, `Class: ${className}${section ? ` - ${section}` : ''}`, S.schoolSub);

  /* ---- Table header (rows 4-5) ---- */
  const h1 = [C('S.N', S.headVert), C('ADM NO.', S.headVert), C('Students Name', S.headTan)];
  const h2 = [C('', S.headVert), C('', S.headVert), C('', S.headTan)];
  subjects.forEach((sub) => {
    h1.push(C(sub, S.headTan), C('', S.headTan));
    h2.push(C('Marks', S.headVert), C('Grade', S.headVert));
  });
  h1.push(C('TOTAL', S.headVert), C('%', S.headVert), C('GRADE', S.headVert));
  h2.push(C('', S.headVert), C('', S.headVert), C('', S.headVert));
  rows.push(h1, h2);

  // vertical merges for fixed columns, horizontal for subject names
  [0, 1, 2].forEach((c) => merges.push({ s: { r: 4, c }, e: { r: 5, c } }));
  subjects.forEach((_, i) => {
    const c = 3 + i * 2;
    merges.push({ s: { r: 4, c }, e: { r: 4, c: c + 1 } });
  });
  [last - 2, last - 1, last].forEach((c) => merges.push({ s: { r: 4, c }, e: { r: 5, c } }));

  /* ---- Student rows ---- */
  students.forEach((st, i) => {
    const row = [
      C(i + 1, S.sn),
      C(st.Admission_Number || '', S.markPlain),
      C(st.Student_Name || '', S.name),
    ];
    subjects.forEach((sub) => {
      const m = st.marks?.[sub];
      row.push(
        C(m ? m.obtained || 0 : '', S.markPlain),
        C(m ? subjectGrade(m, grades) : '', S.mark),
      );
    });
    row.push(
      C(st.obtained || 0, S.mark),
      C(round(st.percentage || 0, 1), S.markPlain),
      C(overallGrade(st, grades), S.mark),
    );
    rows.push(row);
  });

  /* ---- AVERAGE row ---- */
  const avgRow = [C('', S.avgVal), C('', S.avgVal), C('AVERAGE', S.avgLabel)];
  subjects.forEach((sub) => {
    const vals = students.filter((st) => st.marks?.[sub]).map((st) => st.marks[sub].obtained || 0);
    const avg = vals.length ? round(vals.reduce((a, b) => a + b, 0) / vals.length) : '';
    avgRow.push(C(avg, S.avgVal), C('', S.avgVal));
  });
  const totAvg = students.length
    ? round(students.reduce((a, st) => a + (st.obtained || 0), 0) / students.length)
    : '';
  const pctAvg = students.length
    ? round(students.reduce((a, st) => a + (st.percentage || 0), 0) / students.length, 1)
    : '';
  avgRow.push(C(totAvg, S.avgVal), C(pctAvg, S.avgVal), C('', S.avgVal));
  rows.push(avgRow);

  /* ---- Grade distribution table ---- */
  rows.push(new Array(totalCols).fill(null).map(() => C('', {}))); // spacer

  const gStart = rows.length;
  const gCols = 1 + nSub + 1; // GRADES | subjects | OVERALL
  const gLast = gCols - 1;

  const gFull = (r, text) => {
    const row = new Array(gCols).fill(null).map(() => C('', S.blueTitle));
    row[0] = C(text, S.blueTitle);
    rows.push(row);
    merges.push({ s: { r, c: 0 }, e: { r, c: gLast } });
  };
  gFull(gStart, `CLASS ${className}${section ? ` - ${section}` : ''}`);
  gFull(gStart + 1, exam?.Exam_Name || '');

  const gHead = [C('GRADES', S.gradeLabel)];
  subjects.forEach((sub) => gHead.push(C(sub.toUpperCase(), S.gradeHead)));
  gHead.push(C('OVERALL', S.gradeHead));
  rows.push(gHead);

  // counts
  const bandRows = [...grades.map((g) => g.grade), 'ABSENT', 'TOTAL'];
  bandRows.forEach((band) => {
    const row = [C(band, S.gradeLabel)];
    subjects.forEach((sub) => {
      const withEntry = students.filter((st) => st.marks?.[sub]);
      let count;
      if (band === 'TOTAL') count = withEntry.length;
      else if (band === 'ABSENT') count = withEntry.filter((st) => subjectGrade(st.marks[sub], grades) === 'AB').length;
      else count = withEntry.filter((st) => subjectGrade(st.marks[sub], grades) === band).length;
      row.push(C(count, S.gradeVal));
    });
    let oCount;
    if (band === 'TOTAL') oCount = students.length;
    else if (band === 'ABSENT') oCount = students.filter((st) => overallGrade(st, grades) === 'AB').length;
    else oCount = students.filter((st) => overallGrade(st, grades) === band).length;
    row.push(C(oCount, S.gradeVal));
    rows.push(row);
  });

  /* ---- Worksheet ---- */
  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws['!merges'] = merges;

  const cols = [{ wch: 4 }, { wch: 8 }, { wch: 26 }];
  subjects.forEach(() => cols.push({ wch: 6 }, { wch: 6 }));
  cols.push({ wch: 7 }, { wch: 6 }, { wch: 7 });
  ws['!cols'] = cols;

  const rowHeights = [];
  rowHeights[0] = { hpt: 24 };
  rowHeights[4] = { hpt: 16 };
  rowHeights[5] = { hpt: 48 }; // rotated sub-headers
  rowHeights[gStart + 2] = { hpt: 55 }; // rotated grade table header
  ws['!rows'] = rowHeights;

  return ws;
};

/* ------------------------------------------------------------------ */
/* Overall + subject-wise sheet                                        */
/* ------------------------------------------------------------------ */

const sectionLabel = (sec) => `${sec.className}${sec.section ? `-${sec.section}` : ''}`;

/**
 * One sheet: OVERALL grade matrix (columns = every class+section) followed
 * by a matrix per subject. Classes where a subject has no data show 0.
 */
const buildOverallSheet = (sections, subjects, exam, grades) => {
  const nCols = 1 + sections.length + 2; // Grade | classes | Total | %
  const last = nCols - 1;
  const rows = [];
  const merges = [];
  const bandRows = [...grades.map((g) => g.grade), 'AB', 'TOTAL'];

  const title = (text, style) => {
    const r = rows.length;
    const row = new Array(nCols).fill(null).map(() => C('', style));
    row[0] = C(text, style);
    rows.push(row);
    merges.push({ s: { r, c: 0 }, e: { r, c: last } });
  };
  const spacer = () => rows.push([C('', {})]);

  /**
   * Append header + band rows. `getGrade(student)` returns the grade label,
   * 'AB' for absent, or null when the student has no entry (not counted).
   */
  const matrix = (getGrade) => {
    const head = [C('Grade', S.grayHead)];
    sections.forEach((sec) => head.push(C(sectionLabel(sec), S.grayHead)));
    head.push(C('Total', S.grayHead), C('%', S.grayHead));
    rows.push(head);

    const perSection = sections.map((sec) => sec.students.map(getGrade).filter((g) => g !== null));
    const countFor = (gs, band) => {
      if (band === 'TOTAL') return gs.length;
      if (band === 'AB') return gs.filter((g) => g === 'AB').length;
      return gs.filter((g) => g === band).length;
    };
    const grand = perSection.reduce((a, gs) => a + gs.length, 0);

    bandRows.forEach((band) => {
      const row = [C(band, S.grayHead)];
      let total = 0;
      perSection.forEach((gs) => {
        const c = countFor(gs, band);
        total += c;
        row.push(C(c, S.gradeVal));
      });
      row.push(C(total, S.gradeVal));
      row.push(C(grand ? round((total / grand) * 100, 1) : 0, S.gradeVal));
      rows.push(row);
    });
  };

  title('OVERALL', S.bigTitle);
  matrix((st) => overallGrade(st, grades));
  spacer();

  title('Subject Wise Grading', S.graySub);
  subjects.forEach((sub) => {
    title(sub.toUpperCase(), S.graySub);
    matrix((st) => (st.marks?.[sub] ? subjectGrade(st.marks[sub], grades) : null));
    spacer();
  });

  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws['!merges'] = merges;
  const cols = [{ wch: 8 }];
  sections.forEach(() => cols.push({ wch: 6 }));
  cols.push({ wch: 7 }, { wch: 6 });
  ws['!cols'] = cols;
  ws['!rows'] = [{ hpt: 26 }];
  return ws;
};

/* ------------------------------------------------------------------ */
/* Class average sheet                                                 */
/* ------------------------------------------------------------------ */

/**
 * One row per class+section: students, per-subject average marks,
 * average total and average percentage. Subjects not taught show 0.
 */
const buildAveragesSheet = (sections, subjects, exam) => {
  const nCols = 2 + subjects.length + 2; // Class | Students | subjects | Avg Total | Avg %
  const last = nCols - 1;
  const rows = [];
  const merges = [];

  const title = (r, text, style) => {
    const row = new Array(nCols).fill(null).map(() => C('', style));
    row[0] = C(text, style);
    rows.push(row);
    merges.push({ s: { r, c: 0 }, e: { r, c: last } });
  };
  title(0, 'CLASS AVERAGES', S.bigTitle);
  title(1, exam?.Exam_Name || '', S.graySub);

  const head = [C('Class', S.grayHead), C('Students', S.grayHead)];
  subjects.forEach((sub) => head.push(C(sub.toUpperCase(), S.gradeHead)));
  head.push(C('Avg Total', S.grayHead), C('Avg %', S.grayHead));
  rows.push(head);

  sections.forEach((sec) => {
    const row = [C(sectionLabel(sec), S.grayHead), C(sec.students.length, S.gradeVal)];
    subjects.forEach((sub) => {
      // average percentage (plain number), not raw marks
      const vals = sec.students.filter((st) => st.marks?.[sub]).map((st) => {
        const m = st.marks[sub];
        return m.full ? ((m.obtained || 0) / m.full) * 100 : 0;
      });
      row.push(C(vals.length ? round(vals.reduce((a, b) => a + b, 0) / vals.length, 1) : 0, S.gradeVal));
    });
    const n = sec.students.length;
    const avgTot = n ? round(sec.students.reduce((a, st) => a + (st.obtained || 0), 0) / n) : 0;
    const avgPct = n ? round(sec.students.reduce((a, st) => a + (st.percentage || 0), 0) / n, 1) : 0;
    row.push(C(avgTot, S.gradeVal), C(avgPct, S.gradeVal));
    rows.push(row);
  });

  // overall row across all students
  const all = sections.flatMap((sec) => sec.students);
  const oRow = [C('OVERALL', S.grayHead), C(all.length, S.grayHead)];
  subjects.forEach((sub) => {
    const vals = all.filter((st) => st.marks?.[sub]).map((st) => {
      const m = st.marks[sub];
      return m.full ? ((m.obtained || 0) / m.full) * 100 : 0;
    });
    oRow.push(C(vals.length ? round(vals.reduce((a, b) => a + b, 0) / vals.length, 1) : 0, S.grayHead));
  });
  oRow.push(
    C(all.length ? round(all.reduce((a, st) => a + (st.obtained || 0), 0) / all.length) : 0, S.grayHead),
    C(all.length ? round(all.reduce((a, st) => a + (st.percentage || 0), 0) / all.length, 1) : 0, S.grayHead),
  );
  rows.push(oRow);

  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws['!merges'] = merges;
  const cols = [{ wch: 9 }, { wch: 8 }];
  subjects.forEach(() => cols.push({ wch: 7 }));
  cols.push({ wch: 9 }, { wch: 7 });
  ws['!cols'] = cols;
  ws['!rows'] = [{ hpt: 24 }, { hpt: 16 }, { hpt: 55 }]; // rotated subject headers
  return ws;
};

/* ------------------------------------------------------------------ */
/* Class-group summary sheet                                           */
/* ------------------------------------------------------------------ */

const normToken = (s) => String(s || '').toLowerCase().replace(/[\s\-_.]/g, '');

/**
 * One sheet with a grade matrix per configured group, laid out side by side.
 * A group token matches a class+section label or a bare class name
 * (which then includes all of its sections).
 */
const buildGroupsSheet = (cfgGroups, sections, grades) => {
  const tables = cfgGroups
    .map((cfg) => {
      const tokens = (cfg.classes || []).map(normToken);
      const members = sections.filter(
        (sec) => tokens.includes(normToken(`${sec.className}${sec.section || ''}`))
          || tokens.includes(normToken(sec.className)),
      );
      return { name: cfg.name, members };
    })
    .filter((t) => t.members.length);
  if (!tables.length) return null;

  const bandRows = [...grades.map((g) => g.grade), 'AB', 'TOTAL'];
  const nRows = 3 + bandRows.length; // title(2) + header + bands
  const grid = Array.from({ length: nRows }, () => []);
  const merges = [];
  const colWidths = [];
  let col = 0;

  tables.forEach((t) => {
    const w = 1 + t.members.length + 1; // Grade | members | TOTAL

    // two-row merged title box
    grid[0][col] = C(t.name, S.bigTitle);
    for (let c = col + 1; c < col + w; c++) grid[0][c] = C('', S.bigTitle);
    for (let c = col; c < col + w; c++) grid[1][c] = C('', S.bigTitle);
    merges.push({ s: { r: 0, c: col }, e: { r: 1, c: col + w - 1 } });

    grid[2][col] = C('Grade', S.grayHead);
    t.members.forEach((sec, i) => { grid[2][col + 1 + i] = C(sectionLabel(sec), S.grayHead); });
    grid[2][col + w - 1] = C('TOTAL', S.grayHead);

    const perMember = t.members.map((sec) => sec.students.map((st) => overallGrade(st, grades)));
    bandRows.forEach((band, bi) => {
      const r = 3 + bi;
      grid[r][col] = C(band, S.grayHead);
      let total = 0;
      perMember.forEach((gs, i) => {
        let c;
        if (band === 'TOTAL') c = gs.length;
        else if (band === 'AB') c = gs.filter((g) => g === 'AB').length;
        else c = gs.filter((g) => g === band).length;
        total += c;
        grid[r][col + 1 + i] = C(c, S.gradeVal);
      });
      grid[r][col + w - 1] = C(total, S.gradeVal);
    });

    colWidths.push({ wch: 8 });
    t.members.forEach(() => colWidths.push({ wch: 6 }));
    colWidths.push({ wch: 7 }, { wch: 2 }); // TOTAL + gap
    col += w + 1;
  });

  const ws = XLSX.utils.aoa_to_sheet(grid);
  ws['!merges'] = merges;
  ws['!cols'] = colWidths;
  ws['!rows'] = [{ hpt: 22 }, { hpt: 14 }];
  return ws;
};

/* ------------------------------------------------------------------ */
/* Public API                                                          */
/* ------------------------------------------------------------------ */

/**
 * Export one workbook with a sheet per class+section, plus an Overall +
 * subject-wise sheet and (when configured) a class-group summary sheet.
 * @param {object} data        result payload { exam, subjects, students }
 * @param {Array}  grades      grade bands [{ grade, min, max }]
 * @param {object} school      { schoolName, schoolAddress } (optional)
 * @param {Array}  classGroups [{ name, classes: ['Nur', '1-A'] }] (optional)
 */
export const exportClassWiseExcel = (data, grades = [], school = null, classGroups = []) => {
  const { exam, subjects = [], students = [] } = data || {};
  if (!students.length) return;

  // Group by class + section
  const map = new Map();
  students.forEach((st) => {
    const key = `${st.Class || '—'}|${st.Section || ''}`;
    if (!map.has(key)) {
      map.set(key, { className: st.Class || '—', section: st.Section || '', students: [] });
    }
    map.get(key).students.push(st);
  });

  const groups = [...map.values()].sort(
    (a, b) => a.className.localeCompare(b.className, undefined, { numeric: true })
      || a.section.localeCompare(b.section),
  );

  const wb = XLSX.utils.book_new();
  const usedNames = new Set();

  groups.forEach((group) => {
    // only subjects that actually have marks in this class/section
    group.subjects = subjects.filter((sub) => group.students.some((st) => st.marks?.[sub]));
    if (!group.subjects.length) return;

    group.students.sort((a, b) => String(a.Admission_Number || '').localeCompare(
      String(b.Admission_Number || ''), undefined, { numeric: true },
    ));

    let name = `${group.className}${group.section ? `-${group.section}` : ''}`
      .replace(/[\\/?*[\]:]/g, ' ').trim().slice(0, 31) || 'Sheet';
    let n = 1;
    while (usedNames.has(name)) name = `${name.slice(0, 28)}_${n++}`;
    usedNames.add(name);

    XLSX.utils.book_append_sheet(wb, buildSheet(group, exam, grades, school), name);
  });

  // Overall + subject-wise grading sheet (all classes as columns)
  XLSX.utils.book_append_sheet(wb, buildOverallSheet(groups, subjects, exam, grades), 'Overall');

  // Per-class average marks sheet
  XLSX.utils.book_append_sheet(wb, buildAveragesSheet(groups, subjects, exam), 'Class Average');

  // Class-group summary sheet (admin-configured groups)
  if (Array.isArray(classGroups) && classGroups.length) {
    const gws = buildGroupsSheet(classGroups, groups, grades);
    if (gws) XLSX.utils.book_append_sheet(wb, gws, 'Class Groups');
  }

  if (!wb.SheetNames.length) return;
  const fileName = `${(exam?.Exam_Name || 'Results').replace(/[\\/:*?"<>|]/g, '')} - Class Wise.xlsx`;
  XLSX.writeFile(wb, fileName);
};
